import React, { useState, useEffect } from 'react';
import { Scale, ArrowRight } from 'lucide-react';

interface Conversion {
  from: string;
  to: string;
  value: number;
}

interface ConversionCategory {
  name: string;
  units: string[];
  conversions: Conversion[];
}

const conversionData: ConversionCategory[] = [
  {
    name: 'Volume',
    units: ['teaspoon', 'tablespoon', 'fluid ounce', 'cup', 'pint', 'quart', 'gallon', 'milliliter', 'liter'],
    conversions: [
      { from: 'teaspoon', to: 'milliliter', value: 4.92892 },
      { from: 'tablespoon', to: 'milliliter', value: 14.7868 },
      { from: 'fluid ounce', to: 'milliliter', value: 29.5735 },
      { from: 'cup', to: 'milliliter', value: 236.588 },
      { from: 'pint', to: 'milliliter', value: 473.176 },
      { from: 'quart', to: 'milliliter', value: 946.353 },
      { from: 'gallon', to: 'milliliter', value: 3785.41 },
      { from: 'liter', to: 'milliliter', value: 1000 }
    ]
  },
  {
    name: 'Weight',
    units: ['ounce', 'pound', 'gram', 'kilogram'],
    conversions: [
      { from: 'ounce', to: 'gram', value: 28.3495 },
      { from: 'pound', to: 'gram', value: 453.592 },
      { from: 'kilogram', to: 'gram', value: 1000 }
    ]
  },
  {
    name: 'Temperature',
    units: ['Fahrenheit', 'Celsius'],
    conversions: [
      {
        from: 'Fahrenheit',
        to: 'Celsius',
        value: 32 * 5/9
      },
      {
        from: 'Celsius',
        to: 'Fahrenheit',
        value: 9/5 * 32
      }
    ]
  }
];

const QuantityConverter: React.FC = () => {
  const [category, setCategory] = useState<string>('Volume');
  const [fromUnit, setFromUnit] = useState<string>('cup');
  const [toUnit, setToUnit] = useState<string>('milliliter');
  const [fromValue, setFromValue] = useState<string>('1');
  const [toValue, setToValue] = useState<string>('');
  const [recentConversions, setRecentConversions] = useState<string[]>([]);

  const getCategoryUnits = () => {
    const selectedCategory = conversionData.find(c => c.name === category);
    return selectedCategory ? selectedCategory.units : [];
  };

  const convert = () => {
    if (!fromValue || isNaN(parseFloat(fromValue))) {
      setToValue('');
      return;
    }

    const fromValueNum = parseFloat(fromValue);

    // Handle temperature separately
    if (category === 'Temperature') {
      if (fromUnit === 'Fahrenheit' && toUnit === 'Celsius') {
        setToValue(((fromValueNum - 32) * 5 / 9).toFixed(2));
      } else if (fromUnit === 'Celsius' && toUnit === 'Fahrenheit') {
        setToValue(((fromValueNum * 9 / 5) + 32).toFixed(2));
      } else {
        setToValue(fromValue);
      }
      addToRecentConversions();
      return;
    }

    // For other categories
    const selectedCategory = conversionData.find(c => c.name === category);
    if (!selectedCategory) return;

    // If from and to are the same
    if (fromUnit === toUnit) {
      setToValue(fromValue);
      addToRecentConversions();
      return;
    }

    // Convert to base unit first (milliliter for volume, gram for weight)
    let baseValue = 0;
    const baseUnit = category === 'Volume' ? 'milliliter' : 'gram';

    if (fromUnit === baseUnit) {
      baseValue = fromValueNum;
    } else {
      const conversion = selectedCategory.conversions.find(c => c.from === fromUnit && c.to === baseUnit);
      if (conversion) {
        baseValue = fromValueNum * conversion.value;
      }
    }

    // Then convert from base unit to target unit
    if (toUnit === baseUnit) {
      setToValue(baseValue.toFixed(2));
    } else {
      const conversion = selectedCategory.conversions.find(c => c.from === toUnit && c.to === baseUnit);
      if (conversion) {
        setToValue((baseValue / conversion.value).toFixed(2));
      }
    }

    addToRecentConversions();
  };

  const addToRecentConversions = () => {
    const conversionText = `${fromValue} ${fromUnit} = ${toValue} ${toUnit}`;
    setRecentConversions(prev => {
      const withoutDuplicates = prev.filter(c => c !== conversionText);
      return [conversionText, ...withoutDuplicates].slice(0, 5);
    });
  };

  const swapUnits = () => {
    const tempFromUnit = fromUnit;
    const tempToUnit = toUnit;
    const tempFromValue = fromValue;
    const tempToValue = toValue;

    setFromUnit(tempToUnit);
    setToUnit(tempFromUnit);
    setFromValue(tempToValue);
    setToValue(tempFromValue);
  };

  useEffect(() => {
    convert();
  }, [category, fromUnit, toUnit, fromValue]);

  useEffect(() => {
    // Set default units when category changes
    const units = getCategoryUnits();
    if (units.length > 0) {
      setFromUnit(units[0]);
      setToUnit(units[units.length > 1 ? 1 : 0]);
    }
  }, [category]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Quantity Converter</h1>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Category
          </label>
          <div className="flex flex-wrap gap-2">
            {conversionData.map(cat => (
              <button
                key={cat.name}
                onClick={() => setCategory(cat.name)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${category === cat.name
                    ? 'bg-secondary-700 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              From
            </label>
            <div className="md:flex gap-3">
              <input
                type="number"
                value={fromValue}
                onChange={(e) => setFromValue(e.target.value)}
                className="flex w-full sm:w-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <select
                value={fromUnit}
                onChange={(e) => setFromUnit(e.target.value)}
                className="flex px-3 py-2 mt-4 sm:mt-0 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {getCategoryUnits().map(unit => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={swapUnits}
              className="p-2 rounded-full border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowRight size={20} className="text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              To
            </label>
            <div className="md:flex gap-3">
              <input
                type="text"
                value={toValue}
                readOnly
                className="flex w-full sm:w-auto px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
              />
              <select
                value={toUnit}
                onChange={(e) => setToUnit(e.target.value)}
                className="flex px-3 mt-4 sm:mt-0 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {getCategoryUnits().map(unit => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {recentConversions.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Recent Conversions</h3>
            <ul className="space-y-2">
              {recentConversions.map((conversion, index) => (
                <li
                  key={index}
                  className="text-gray-600 dark:text-gray-400 text-sm bg-gray-50 dark:bg-gray-700/50 py-2 px-3 rounded-lg"
                >
                  {conversion}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Common Cooking Conversions</h2>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-4 py-3 bg-gray-50 dark:bg-gray-900 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  From
                </th>
                <th className="px-4 py-3 bg-gray-50 dark:bg-gray-900 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  To
                </th>
                <th className="px-4 py-3 bg-gray-50 dark:bg-gray-900 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Conversion
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">1 tablespoon</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">3 teaspoons</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">Exact</td>
              </tr>
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">1 cup</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">16 tablespoons</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">Exact</td>
              </tr>
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">1 cup</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">8 fluid ounces</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">Exact</td>
              </tr>
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">1 cup</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">237 ml</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">Approximate</td>
              </tr>
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">1 pint</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">2 cups</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">Exact</td>
              </tr>
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">1 pound</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">454 grams</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">Approximate</td>
              </tr>
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">1 ounce</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">28 grams</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">Approximate</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QuantityConverter;